import * as React from 'react';
import { ClickBustingConfig } from '../config/ClickBusting';
import { conditional, makeTestId } from '../utils/Declarative';
import { InputLabelWidget } from './InputLabelWidget';

const SwitcherTextGroupNamePrefix = 'SwitcherTextGroup';

export type SwitcherTextBaseStyle = Record<
  'activeSelectedToggle' |
  'activeUnselectedToggle' |
  'container' |
  'disabledUnselectedToggle' |
  'disabledSelectedToggle' |
  'firstToggle' |
  'focusToggle' |
  'hoverSelectedToggle' |
  'hoverUnselectedToggle' |
  'lastToggle' |
  'selectedToggle' |
  'toggleBase' |
  'unselectedToggle',
  React.CSSProperties
>;

export interface SwitcherTextBaseProps {
  ariaLabel?: string;
  checkedIndex: number;
  className?: string;
  disabled?: boolean;
  handleChange: (idx: number) => void;
  /** The input name attribute */
  name?: string;
  style: SwitcherTextBaseStyle;
  testId: string;
  toggleTexts: string[];
}

interface SwitcherButtonProps {
  checked?: boolean;
  disabled?: boolean;
  groupName: string;
  handleChange: (idx: number) => void;
  activeStyle: React.CSSProperties;
  disabledStyle: React.CSSProperties;
  focusStyle: React.CSSProperties;
  hoverStyle: React.CSSProperties;
  idx: number;
  style: React.CSSProperties;
  testId: string;
}

/**
 * The SwitchTextButton is the individual button within a switcher.  It contains the text and
 * can be toggled.  The underlying input is a radio button.
 */
class SwitcherTextButton extends React.Component<SwitcherButtonProps, {}> {
  private handleChange = (): void => {
    if (!this.props.checked) {
      this.props.handleChange(this.props.idx);
    }
  }

  public render(): React.ReactElement {
    return (
      // eslint-disable-next-line deprecation/deprecation
      <InputLabelWidget
        disabled={this.props.disabled}
        interactiveStyles={{
          active: this.props.activeStyle,
          disabled: this.props.disabledStyle,
          focusVisible: this.props.focusStyle,
          hover: this.props.hoverStyle,
        }}
        style={this.props.style}
        testId={this.props.testId}
      >
        <input
          checked={this.props.checked}
          data-tb-test-id={makeTestId(this.props.testId, 'input')}
          disabled={this.props.disabled}
          name={this.props.groupName}
          onChange={this.handleChange}
          style={{
            opacity: 0,
            position: 'absolute'
          }}
          type='radio'
        />
        {this.props.children}
      </InputLabelWidget>
    );
  }
}

/**
 * The SwitchTextBase is the container of the switcher buttons.  It maintains the state of what
 * switcher button is active and creates the group id for the underlying radio buttons.
 */
export class SwitcherTextBase extends React.Component<SwitcherTextBaseProps> {
  private static groupIdIncrementor: number = 0;
  private groupId: number = SwitcherTextBase.groupIdIncrementor++;

  private getToggleStyling(currentSwitcherIdx: number, isChecked: boolean): React.CSSProperties {
    const isFirst = currentSwitcherIdx === 0;
    const isLast = currentSwitcherIdx === this.props.toggleTexts.length - 1;

    return {
      ...this.props.style.toggleBase,
      ...conditional(isFirst, this.props.style.firstToggle),
      ...conditional(isLast, this.props.style.lastToggle),
      ...(isChecked ? this.props.style.selectedToggle : this.props.style.unselectedToggle),
    };
  }

  public render(): React.ReactElement {
    const groupName = SwitcherTextGroupNamePrefix + String(this.groupId);

    const switchButtons = this.props.toggleTexts.map((toggleText: string, idx: number) => {
      const isChecked: boolean = this.props.checkedIndex === idx;
      const toggleStyle = this.getToggleStyling(idx, isChecked);
      const activeStyle = isChecked ? this.props.style.activeSelectedToggle : this.props.style.activeUnselectedToggle;
      const hoverStyle = isChecked ? this.props.style.hoverSelectedToggle : this.props.style.hoverUnselectedToggle;
      const disabledStyle = isChecked ? this.props.style.disabledSelectedToggle : this.props.style.disabledUnselectedToggle;

      return <SwitcherTextButton
        activeStyle={activeStyle}
        checked={isChecked}
        children={toggleText}
        disabled={this.props.disabled}
        disabledStyle={disabledStyle}
        focusStyle={this.props.style.focusToggle}
        groupName={this.props.name || groupName}
        handleChange={this.props.handleChange}
        hoverStyle={hoverStyle}
        idx={idx}
        key={idx}
        style={toggleStyle}
        testId={makeTestId(this.props.testId, idx === 0 ? 'left' : 'right')}
      />;
    });

    return <div
      aria-label={this.props.ariaLabel}
      children={switchButtons}
      className={[ClickBustingConfig.suppressionClassName, this.props.className].join(' ')}
      role='radiogroup'
      style={this.props.style.container}
    />;
  }
}
